import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { Button, Field, Page } from "../../components";
import { AuthLayout } from "./AuthLayout";
import { client, unwrap } from "../../api/client";
import { ApiError, describeApiError, isApiError } from "../../api/errors";
import { useFeatures } from "../../api/features";
import { t } from "../../i18n/t";
import { legalVersion } from "../../legal/documents";
import { MIN_PASSWORD_LENGTH } from "./passwordPolicy";

/**
 * P1a §7: `POST /api/v1/auth/register` always answers `202` — whether or not the email already has an account
 * (anti-enumeration) — so this screen has exactly one success state, shown after the request is accepted, and
 * it never claims the account was newly created.
 */
export function RegisterScreen(): JSX.Element {
  const { features } = useFeatures();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  // xmg-sec-opsdocs (P1): consent to the terms of use and privacy policy is required to create an account; the
  // server refuses a registration without it and records the document versions sent below.
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendFailed, setResendFailed] = useState(false);

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        client.POST("/api/v1/auth/register", {
          body: {
            email,
            password,
            display_name: displayName === "" ? null : displayName,
            terms_accepted: true,
            legal_version: legalVersion(),
          },
        }),
      );
      setSubmitted(true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setError(t("auth.login.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else if (
        caught instanceof ApiError &&
        caught.is("forbidden") &&
        caught.details.reason === "signup_restricted"
      ) {
        setError(t("auth.register.by_invitation_refused"));
      } else if (isApiError(caught)) {
        setError(describeApiError(caught, t("error.unexpected")));
      } else {
        setError(t("error.unexpected"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onResend(): Promise<void> {
    setBusy(true);
    setResent(false);
    setResendFailed(false);
    try {
      await unwrap(client.POST("/api/v1/auth/verify-email/resend", { body: { email } }));
      setResent(true);
    } catch {
      // Anti-enumeration (P1a §7): the same confirmation whether or not the account exists - for a 2xx only.
      setResendFailed(true);
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    // AuthFlows card 02: the centred check-your-email card with the mail icon tile.
    return (
      <AuthLayout>
        <Page narrow title={t("auth.register.success_title")}>
          <div className="card card--auth card--auth--center">
            <span className="authicon authicon--accent" aria-hidden="true">
              <Mail size={24} strokeWidth={2} />
            </span>
            <p className="authcenter">{t("auth.register.success_body", { email })}</p>
            <Button className="button--auth" onClick={() => void onResend()} disabled={busy}>
              {t("auth.register.resend")}
            </Button>
            {resent ? <p role="status">{t("auth.register.resend_sent")}</p> : null}
            {resendFailed ? <p role="alert">{t("auth.register.resend_failed")}</p> : null}
            <p className="authcenter">
              <Link to="/auth/login">{t("auth.register.login_link")}</Link>
            </p>
          </div>
        </Page>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Page narrow title={t("auth.register.title")}>
        <form onSubmit={(event) => void onSubmit(event)} className="card card--auth">
          {features.signup_restricted ? (
            <p role="note" className="authcenter">
              <strong>{t("auth.register.by_invitation_title")}</strong>{" "}
              {t("auth.register.by_invitation_body")}
            </p>
          ) : null}
          <Field label={t("auth.register.email")}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <Field label={t("auth.register.password")} hint={t("auth.register.password_hint")} error={error}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
          <Field label={t("auth.register.display_name")}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            )}
          </Field>
          <label className="consentcheck">
            <input
              type="checkbox"
              required
              checked={consented}
              onChange={(event) => setConsented(event.target.checked)}
            />
            <span>
              {t("auth.register.consent_prefix")} <Link to="/terms">{t("legal.terms_link")}</Link>{" "}
              {t("auth.register.consent_middle")} <Link to="/privacy">{t("legal.privacy_link")}</Link>.
            </span>
          </label>
          <Button
            type="submit"
            variant="primary"
            className="button--auth"
            disabled={busy || email === "" || password.length < MIN_PASSWORD_LENGTH || !consented}
          >
            {t("auth.register.submit")}
          </Button>
          <p className="authcenter">
            {t("auth.register.login_prompt")} <Link to="/auth/login">{t("auth.register.login_link")}</Link>
          </p>
        </form>
      </Page>
    </AuthLayout>
  );
}
